import { loadCanonicalSkill } from '../skill-engine/facade.js';
import type { CompiledSkillPlanV3 } from '../skill-engine/composite-compiler.js';
import type { ExpandedTransform } from '../skill-engine/composite-types.js';
import type { CanonicalEffectNodeV2, CanonicalSkillV2 } from '../skill-engine/types.js';
import { isIdentityTransform, resolveAttackPose, stageCastPose, type AttackPose, type CastPose } from '../sim/attack-pose.js';
import { effectiveProjectileDelivery } from '../sim/projectile-accuracy.js';
import type { PrimaryStats } from '../sim/primary-stats.js';
import { blastCatalystRadiusM } from '../sim/catalyst-basic.js';
import type { PlayableWeaponId } from '../reference/weapon-progression-v0.js';
import { skillRangePreviews, type RangePreview, type ShapePreview } from './range-preview.js';

type Shape = ShapePreview;

/** One attack unit of a release: its footprint, where the cast pose puts it, and the blast it sets off where it ends. */
export interface FootprintMember {
  readonly stageId: string;
  readonly shape: Shape;
  /** The runtime's own placement of this unit relative to the cast pose (`resolveAttackPose` turns it into a world pose). */
  readonly transform: ExpandedTransform;
  /** Its stage's formation, which decides whether the cast's aim turns it (`stageCastPose`). */
  readonly formationKind: string;
  /** Blast radius set off at the end of the member's reach, when the skill detonates. */
  readonly blastRadiusM?: number;
  /** A held Blast Catalyst's splash where a magic hit lands, anywhere along the member's reach. */
  readonly splashRadiusM?: number;
}

/** An orbit stage: the circle its orbs travel on and where each starts. */
export interface OrbitFootprint {
  readonly stageId: string;
  readonly radiusM: number;
  readonly orbRadiusM: number;
  readonly angularSpeedRadPerS: number;
  readonly initialPhasesRad: readonly number[];
}

/**
 * Everything one release of a crafted skill covers, built from the compiled
 * plan the server runs. Every member is posed by the release's aim
 * (`authoritative-world-core.ts` `releaseAimRad`): the pointer for a
 * pointer-aimed slot; for an automatic one, toward the nearest enemy, else
 * the pointer.
 */
export interface FootprintPreview {
  readonly kind: 'footprint';
  readonly members: readonly FootprintMember[];
  readonly orbits: readonly OrbitFootprint[];
  readonly label: string;
}

const IDENTITY: ExpandedTransform = { x: 0, y: 0, directionRad: 0 };

/** Where a member is released from and which way it faces: the runtime's own pose (`stageCastPose`, `resolveAttackPose`). */
export function memberPose(cast: CastPose, member: FootprintMember): AttackPose {
  return resolveAttackPose(stageCastPose(cast, member.formationKind), member.transform);
}

/** Radii of every blast an effect tree sets off (`secondary_attack` circles, at any depth). */
function blastRadiiM(effects: readonly CanonicalEffectNodeV2[]): number[] {
  return effects.flatMap((node) => {
    if (node.kind === 'secondary_attack') {
      const shape = node.application.geometry.owner === 'SKILL_OWNED' ? node.application.geometry.shape : undefined;
      return [...(shape?.kind === 'CIRCLE' ? [shape.radiusM] : []), ...blastRadiiM(node.effects)];
    }
    return node.kind === 'condition' || node.kind === 'trigger' ? blastRadiiM(node.children) : [];
  });
}

/**
 * One attack's footprint where the server will put it. Its heading is the
 * release's aim. A skill without `targeting` re-acquires at release
 * (`resolveReacquiredDeliveryTargets`), so from an automatic slot its ground
 * circle lands on the nearest enemy (`center: 'nearest-enemy'`) and from a
 * pointer-aimed slot on the pointer. A formation member that is placed is
 * posed by the formation instead.
 */
function memberShapes(canonical: CanonicalSkillV2, name: string, placed: boolean, stats: PrimaryStats): Shape[] {
  const { application } = canonical;
  const [preview, ...alsoCovered] = skillRangePreviews(application, name);
  if (preview === undefined) return [];
  const reacquires = !placed && canonical.targeting === undefined;
  if (preview.kind === 'corridor' && application.motion.kind === 'PROJECTILE') {
    // The caster's precision stat widens or narrows the arrow at release (`effectiveProjectileDelivery`, applied by `executeSkill`).
    const motion = application.motion;
    const effective = effectiveProjectileDelivery({ type: 'projectile', rangeM: motion.maxTravelM, speedMps: motion.speedMps, radiusM: motion.collisionRadiusM }, stats, canonical.sourceRequirement);
    return [{ ...preview, widthM: effective.type === 'projectile' ? effective.radiusM * 2 : preview.widthM }];
  }
  if (preview.kind !== 'circle') return [preview, ...alsoCovered];
  const { acquisitionRangeM: _acquisition, ...ownGround } = preview;
  if (placed) return [{ ...ownGround, center: 'caster' }];
  return [reacquires && preview.center === 'aim'
    ? { ...ownGround, center: 'nearest-enemy', persistent: application.lifecycle.kind === 'PERSISTENT' }
    : preview];
}

/** The splash a held Blast Catalyst adds to a crafted magic skill's hit (`catalystSplashApplies`); none for any other kind, source or shape. */
function splashRadiusM(canonical: CanonicalSkillV2, weaponId: PlayableWeaponId | undefined, stats: PrimaryStats): number | undefined {
  const { application } = canonical;
  const direct = application.motion.kind === 'PROJECTILE' && application.contactPolicy.maxPierce === undefined && canonical.effects.some((node) => node.kind === 'damage');
  return weaponId === 'BLAST_CATALYST' && canonical.sourceRequirement?.sourceClass === 'MAGIC' && direct ? blastCatalystRadiusM(stats.MAG) : undefined;
}

function compositeFootprint(plan: CompiledSkillPlanV3, name: string, stats: PrimaryStats, weaponId: PlayableWeaponId | undefined): FootprintPreview {
  const placed = plan.stages.some((stage) => stage.units.some((unit) => unit.transforms.some((transform) => !isIdentityTransform(transform))));
  const members: FootprintMember[] = [];
  const orbits: OrbitFootprint[] = [];
  for (const stage of plan.stages) {
    if (stage.orbit !== undefined) {
      orbits.push({ stageId: stage.stageId, radiusM: stage.orbit.orbitRadiusM, orbRadiusM: stage.orbit.contactRadiusM,
        angularSpeedRadPerS: stage.orbit.angularSpeedRadPerS, initialPhasesRad: stage.orbit.initialPhasesRad });
      continue;
    }
    const canonical = stage.runtimePlan.canonicalSkill;
    const shapes = memberShapes(canonical, name, placed, stats);
    if (shapes.length === 0) continue;
    const blastRadiusM = blastRadiiM(canonical.effects)[0];
    const splash = splashRadiusM(canonical, weaponId, stats);
    const formationKind = plan.canonicalSkill.stages.find((authored) => authored.stageId === stage.stageId)?.pattern.formation.kind ?? 'SINGLE';
    for (const unit of stage.units) {
      for (const transform of unit.transforms) {
        for (const shape of shapes) members.push({ stageId: stage.stageId, shape, transform, formationKind, ...(blastRadiusM === undefined ? {} : { blastRadiusM }), ...(splash === undefined ? {} : { splashRadiusM: splash }) });
      }
    }
  }
  return { kind: 'footprint', members, orbits, label: name };
}

/**
 * The footprint of a held crafted skill: what its next release covers.
 *
 * The definition is loaded exactly as the server loads it (`loadCanonicalSkill`
 * with the default cast seed, `src/game/model.ts`), so a composite's members
 * are the runtime's own spatial-pattern expansion rather than a second formula
 * here. A single attack keeps its plain range shape unless it also detonates.
 */
export function craftedSkillPreview(definition: unknown, name: string, stats: PrimaryStats, weaponId?: PlayableWeaponId): RangePreview | undefined {
  // Held skills are previewed every frame; a definition object is immutable, so it is loaded once per set of stats.
  if (typeof definition !== 'object' || definition === null) return buildPreview(definition, name, stats, weaponId);
  const statsKey = JSON.stringify([stats, weaponId]);
  const cached = cache.get(definition);
  if (cached?.statsKey === statsKey) return cached.preview;
  const preview = buildPreview(definition, name, stats, weaponId);
  cache.set(definition, { statsKey, preview });
  return preview;
}

const cache = new WeakMap<object, { readonly statsKey: string; readonly preview: RangePreview | undefined }>();

function buildPreview(definition: unknown, name: string, stats: PrimaryStats, weaponId: PlayableWeaponId | undefined): RangePreview | undefined {
  const loaded = loadCanonicalSkill(definition);
  if (loaded.status === 'READY_COMPOSITE') return compositeFootprint(loaded.plan, name, stats, weaponId);
  if (loaded.status !== 'READY') return undefined;
  const shapes = memberShapes(loaded.skill, name, false, stats);
  const blastRadiusM = blastRadiiM(loaded.skill.effects)[0];
  const splash = splashRadiusM(loaded.skill, weaponId, stats);
  if (shapes.length === 0) return undefined;
  if (shapes.length === 1 && blastRadiusM === undefined && splash === undefined) return shapes[0];
  return { kind: 'footprint', members: shapes.map((shape) => ({ stageId: 'primary', shape, transform: IDENTITY, formationKind: 'SINGLE', ...(blastRadiusM === undefined ? {} : { blastRadiusM }), ...(splash === undefined ? {} : { splashRadiusM: splash }) })), orbits: [], label: name };
}
